import type { HTMLAttributes, ReactNode, ElementType } from 'react';
import { cn } from './utils';

export type HeadingLevel = 1 | 2 | 3 | 4 | 5 | 6;
export type HeadingSize = 'display' | 'h1' | 'h2' | 'h3' | 'h4' | 'h5';
export type HeadingWeight = 'regular' | 'medium' | 'semibold' | 'bold';
export type HeadingColor = 'default' | 'primary' | 'muted' | 'accent' | 'inverse';

export interface HeadingProps extends HTMLAttributes<HTMLHeadingElement> {
  /** Semantic HTML level — drives which h1-h6 element is rendered. */
  level?: HeadingLevel;
  /** Visual size — independent of semantic level (size the heading one way, tag it another). */
  size?: HeadingSize;
  weight?: HeadingWeight;
  color?: HeadingColor;
  /**
   * When `true` (default), size tokens scale up at the `md` breakpoint
   * (e.g. h3 = `text-2xl md:text-3xl`). Set to `false` for a static size
   * at all viewports — useful when the heading needs to stay compact on
   * desktop.
   */
  responsive?: boolean;
  children: ReactNode;
}

const sizeStyles: Record<HeadingSize, string> = {
  display: 'text-5xl md:text-6xl leading-tight',
  h1: 'text-4xl md:text-5xl leading-tight',
  h2: 'text-3xl md:text-4xl leading-tight',
  h3: 'text-2xl md:text-3xl leading-snug',
  h4: 'text-xl md:text-2xl leading-snug',
  h5: 'text-lg md:text-xl leading-snug',
};

const staticSizeStyles: Record<HeadingSize, string> = {
  display: 'text-5xl leading-tight',
  h1: 'text-4xl leading-tight',
  h2: 'text-3xl leading-tight',
  h3: 'text-2xl leading-snug',
  h4: 'text-xl leading-snug',
  h5: 'text-lg leading-snug',
};

const weightStyles: Record<HeadingWeight, string> = {
  regular: 'font-normal',
  medium: 'font-medium',
  semibold: 'font-semibold',
  bold: 'font-bold',
};

const colorStyles: Record<HeadingColor, string> = {
  default: 'text-neutral-800',
  primary: 'text-primary',
  muted: 'text-neutral-600',
  accent: 'text-accent',
  inverse: 'text-white',
};

const baseStyles = 'font-heading';

export function Heading({
  level = 2,
  size,
  weight = 'bold',
  color = 'default',
  responsive = true,
  className = '',
  children,
  ...rest
}: HeadingProps) {
  const Tag = `h${level}` as ElementType;
  const resolvedSize: HeadingSize = size ?? (
    level === 1 ? 'h1' : level === 2 ? 'h2' : level === 3 ? 'h3' : level === 4 ? 'h4' : 'h5'
  );
  const sizeCls = (responsive ? sizeStyles : staticSizeStyles)[resolvedSize];
  const cls = cn(baseStyles, sizeCls, weightStyles[weight], colorStyles[color], className);
  return (
    <Tag className={cls} {...rest}>
      {children}
    </Tag>
  );
}
